/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BackupRecord } from '../types';
import { uploadItemToFirestore, deleteItemFromFirestore } from '../utils/firebaseSync';
import { computeSha256, verifyBackupIntegrity } from '../utils/cryptoUtils';

/**
 * Backup Repository
 * Manages backup documents, cryptographic signatures, and immutability.
 */
export class BackupRepository {
  private static STORAGE_KEY = 'insumedic_backups_v3';

  static getLocalBackups(): BackupRecord[] {
    const raw = localStorage.getItem(this.STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  static saveLocalBackups(backups: BackupRecord[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(backups));
  }

  static async createBackupRecord(
    id: string,
    fechaHora: string,
    tipo: 'AUTOMATICA (18:00)' | 'MANUAL',
    companyId: string,
    createdBy: string,
    data: BackupRecord['data']
  ): Promise<BackupRecord> {
    const hashSha256 = await computeSha256(data);
    
    const newBackup: BackupRecord = {
      id,
      fechaHora,
      timestamp: fechaHora,
      tipo,
      companyId,
      createdBy,
      hashSha256,
      hashAlgorithm: 'SHA-256',
      schemaVersion: '1.0.1',
      status: 'COMPLETED',
      ordenesCount: data.ordenes.length,
      disposicionesCount: data.disposiciones.length,
      usersCount: data.users.length,
      data
    };

    const backups = this.getLocalBackups();
    backups.unshift(newBackup);
    this.saveLocalBackups(backups);
    
    await uploadItemToFirestore<BackupRecord>('backups', newBackup);
    return newBackup;
  }

  static async verifyIntegrity(backup: BackupRecord): Promise<{ valid: boolean; calculatedHash: string; originalHash?: string }> {
    return verifyBackupIntegrity(backup);
  }

  static async deleteBackup(id: string): Promise<void> {
    const backups = this.getLocalBackups().filter(b => b.id !== id);
    this.saveLocalBackups(backups);
    await deleteItemFromFirestore('backups', id);
  }
}
